// @/pages/SuperAdmin/superAdminDashboard.tsx

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield,
  Mail,
  Lock,
  LogOut,
  RefreshCw,
  CheckCircle,
  Clock,
  AlertTriangle,
  ArrowRight,
  FileText,
  Users,
  Search,
  X,
  ExternalLink,
} from "lucide-react";
import logoImg from "@/assets/Sthitih_Logo.png";
import { getAllRequests, getInvoiceByRequest as getInvoiceByRequestId, approveEnterpriseRequest, rejectEnterpriseRequest } from "@/services/billing.service";
import { getPaymentByInvoiceId, verifyPayment } from "@/services/billing.service";
import { provisionEnterprise } from "@/services/program.service";
import { getAllUsers } from "@/services/auth.service";



// Types for toast notifications
interface Toast {
  id: string;
  message: string;
  type: "success" | "error" | "info";
}

const EnterpriseQuotesView: React.FC = () => {
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);

  const [actionModal, setActionModal] = useState<'none' | 'approve' | 'reject' | 'success' | 'verify' | 'provision'>('none');
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getAllRequests();
        const reqs = res.data || [];

        const enrichedReqs = await Promise.all(
          reqs.map(async (req: any) => {
            let invoice = null;
            let payment = null;

            if (req.status !== "DRAFT" && req.status !== "SUBMITTED" && req.status !== "APPROVED") {
              try {
                const invRes = await getInvoiceByRequestId(req.id);
                invoice = invRes.data;
                if (invoice) {
                  try {
                    const payRes = await getPaymentByInvoiceId(invoice.id);
                    payment = payRes.data;
                  } catch (e) { }
                }
              } catch (e) { }
            }
            return { ...req, invoice, payment };
          })
        );

        setRequests(enrichedReqs);
      } catch (err) {
        console.error("Failed to fetch", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (actionModal !== 'none' && actionModal !== 'success') {
          setActionModal('none');
        } else if (actionModal === 'none' && selectedRequest) {
          setSelectedRequest(null);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [actionModal, selectedRequest]);

  const handleConfirmApprove = async () => {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      await approveEnterpriseRequest(selectedRequest.id);
      // Update local state to show APPROVED
      setRequests((prev) =>
        prev.map(r => r.id === selectedRequest.id ? { ...r, status: "APPROVED" } : r)
      );
      setSelectedRequest((prev: any) =>
        prev ? { ...prev, status: "APPROVED" } : prev
      );
      setActionModal('success');
    } catch (err: any) {
      console.error(err);
      alert("Failed to approve request: " + (err.response?.data?.message || err.message));
      setActionModal('none');
    } finally {
      setActionLoading(false);
    }
  };

  const handleProvision = async () => {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      await provisionEnterprise(selectedRequest.id);
      
      // Update local state to show APPROVED / PROVISIONED
      setRequests((prev) =>
        prev.map(r => r.id === selectedRequest.id ? { ...r, status: "APPROVED" } : r)
      );
      setSelectedRequest((prev: any) =>
        prev ? { ...prev, status: "APPROVED" } : prev
      );
      setActionModal('success');
    } catch (err: any) {
      console.error(err);
      alert("Failed to provision enterprise: " + (err.response?.data?.message || err.message));
      setActionModal('none');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifyPayment = async () => {
    if (!selectedRequest || !selectedRequest.payment) return;
    setActionLoading(true);
    try {
      await verifyPayment(selectedRequest.payment.id, {
        remarks: "Verified by Super Admin",
        verifiedBy: "Super Admin",
      });
      
      // Update local state to show payment VERIFIED
      setRequests((prev) =>
        prev.map(r => r.id === selectedRequest.id ? { ...r, payment: { ...r.payment, status: "VERIFIED" } } : r)
      );
      setSelectedRequest((prev: any) =>
        prev ? { ...prev, payment: { ...prev.payment, status: "VERIFIED" } } : prev
      );
      setActionModal('success');
    } catch (err: any) {
      console.error(err);
      alert("Failed to verify payment: " + (err.response?.data?.message || err.message));
      setActionModal('none');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectRemarks.trim()) return;
    setActionLoading(true);
    try {
      await rejectEnterpriseRequest(selectedRequest.id, rejectRemarks);
      // Update local state to show REJECTED
      setRequests((prev) =>
        prev.map(r => r.id === selectedRequest.id ? { ...r, status: "REJECTED" } : r)
      );
      setSelectedRequest((prev: any) =>
        prev ? { ...prev, status: "REJECTED" } : prev
      );
      setActionModal('success');
    } catch (err: any) {
      console.error(err);
      alert("Failed to reject request: " + (err.response?.data?.message || err.message));
      setActionModal('none');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <div className="bg-white border border-pbl-sand-border/40 rounded-2xl p-6 shadow-sm">
        <h3 className="text-lg font-bold text-pbl-green-dark uppercase tracking-wider mb-4">Enterprise Quotes & Payments</h3>
        {loading ? (
          <div className="flex justify-center p-8"><RefreshCw className="w-8 h-8 animate-spin text-pbl-green-medium" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {requests.map((r) => (
              <motion.div
                key={r.id}
                whileHover={{ scale: 1.02 }}
                onClick={() => setSelectedRequest(r)}
                className="p-5 border border-pbl-sand-border/60 rounded-2xl cursor-pointer hover:shadow-lg transition-all bg-[#FAF7F5] hover:bg-white flex flex-col justify-between h-full group"
              >
                <div>
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-black text-pbl-green-dark text-lg truncate pr-2 group-hover:text-pbl-green-main transition-colors">{r.organizationName}</h4>
                    <span className="inline-block px-2.5 py-1 rounded-lg text-[9px] font-extrabold border bg-white border-pbl-sand-border shadow-sm text-pbl-green-dark shrink-0">
                      {r.status === 'APPROVED' ? 'APPROVED' : (r.payment ? r.payment.status : r.status)}
                    </span>
                  </div>
                  <p className="text-xs text-pbl-sand-dark font-bold mb-1 flex items-center gap-1.5"><Mail className="w-3 h-3" /> {r.moderatorEmail}</p>
                  <p className="text-xs font-bold text-pbl-green-medium flex items-center gap-1.5"><Clock className="w-3 h-3" /> {r.billingCycle} - {r.contractDuration}M Plan</p>
                </div>

                <div className="mt-5 pt-4 border-t border-pbl-sand-border/50 flex justify-between items-end bg-white -mx-5 -mb-5 p-5 rounded-b-2xl border-x-0 border-b-0">
                  <div>
                    <p className="text-[9px] text-pbl-sand-dark font-extrabold uppercase tracking-widest mb-0.5">Invoice Total</p>
                    <p className="font-black text-lg text-pbl-green-dark leading-none">
                      {r.invoice ? `₹${r.invoice.grandTotal}` : "-"}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
            {requests.length === 0 && (
              <div className="col-span-full py-12 text-center text-sm font-bold text-pbl-sand-dark bg-[#FAF7F5] rounded-2xl border border-dashed border-pbl-sand-border">
                No enterprise requests found in the system.
              </div>
            )}
          </div>
        )}
      </div>

      <AnimatePresence>
        {selectedRequest && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedRequest(null)}
              className="fixed inset-0 bg-pbl-green-dark/60 backdrop-blur-md"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-[#FAF7F5] rounded-3xl shadow-2xl p-6 sm:p-8 border border-white/20 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            >
              <div className="mb-6 pb-6 border-b border-pbl-sand-border/50 flex justify-between items-start">
                <div>
                  <div className="inline-flex items-center gap-2 mb-3">
                    <span className="px-3 py-1 bg-pbl-green-main text-white text-xs font-black uppercase tracking-wider rounded-lg">
                      {selectedRequest.status === 'APPROVED' ? 'APPROVED' : (selectedRequest.payment ? selectedRequest.payment.status : selectedRequest.status)}
                    </span>
                    <span className="text-xs font-bold text-pbl-sand-dark">
                      REQ: {selectedRequest.requestNumber}
                    </span>
                  </div>
                  <h2 className="text-3xl font-black text-pbl-green-dark">{selectedRequest.organizationName}</h2>
                  <p className="text-sm font-semibold text-pbl-green-medium mt-1 uppercase tracking-wider">{selectedRequest.organizationType}</p>
                </div>

                {selectedRequest.payment?.status === "UNDER_VERIFICATION" && (
                  <div className="flex gap-3">
                    <button
                      onClick={() => setActionModal('reject')}
                      className="px-6 py-3 bg-white text-red-600 border border-red-200 rounded-xl font-bold text-sm shadow-sm hover:bg-red-50 transition-colors"
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => setActionModal('verify')}
                      className="px-6 py-3 bg-pbl-green-main text-white rounded-xl font-bold text-sm shadow-md hover:bg-pbl-green-dark transition-colors"
                    >
                      Verify Payment
                    </button>
                  </div>
                )}
                {selectedRequest.payment?.status === "VERIFIED" && selectedRequest.status !== "APPROVED" && (
                  <div className="flex gap-3">
                    <button
                      onClick={() => setActionModal('provision')}
                      className="px-6 py-3 bg-pbl-green-main text-white rounded-xl font-bold text-sm shadow-md hover:bg-pbl-green-dark transition-colors"
                    >
                      Provision
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Organization Details */}
                <div className="bg-white p-5 rounded-2xl border border-pbl-sand-border shadow-sm">
                  <h4 className="text-xs font-black text-pbl-sand-dark uppercase tracking-widest mb-4 flex items-center gap-2">
                    <Shield className="w-4 h-4" /> Org Details
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div><span className="font-semibold text-pbl-sand-dark">Reg. Number:</span> <span className="font-bold text-pbl-green-dark">{selectedRequest.registrationNumber || "N/A"}</span></div>
                    <div><span className="font-semibold text-pbl-sand-dark">GST Number:</span> <span className="font-bold text-pbl-green-dark">{selectedRequest.gstNumber || "N/A"}</span></div>
                    <div><span className="font-semibold text-pbl-sand-dark">Country:</span> <span className="font-bold text-pbl-green-dark">{selectedRequest.country}</span></div>
                    <div><span className="font-semibold text-pbl-sand-dark">State / City:</span> <span className="font-bold text-pbl-green-dark">{selectedRequest.state}, {selectedRequest.city}</span></div>
                    <div><span className="font-semibold text-pbl-sand-dark">Timezone:</span> <span className="font-bold text-pbl-green-dark">{selectedRequest.timezone}</span></div>
                    <div>
                      <span className="font-semibold text-pbl-sand-dark block mb-1">Billing Address:</span>
                      <div className="p-3 bg-[#FAF7F5] rounded-xl text-xs font-semibold text-pbl-green-dark border border-pbl-sand-border/50">
                        {selectedRequest.billingAddress}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact & Commercials */}
                <div className="space-y-6">
                  <div className="bg-white p-5 rounded-2xl border border-pbl-sand-border shadow-sm">
                    <h4 className="text-xs font-black text-pbl-sand-dark uppercase tracking-widest mb-4 flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Primary Contact
                    </h4>
                    <div className="space-y-3 text-sm">
                      <div><span className="font-semibold text-pbl-sand-dark">Name:</span> <span className="font-bold text-pbl-green-dark">{selectedRequest.moderatorName}</span></div>
                      <div><span className="font-semibold text-pbl-sand-dark">Email:</span> <span className="font-bold text-pbl-green-dark">{selectedRequest.moderatorEmail}</span></div>
                      <div><span className="font-semibold text-pbl-sand-dark">Phone:</span> <span className="font-bold text-pbl-green-dark">{selectedRequest.moderatorPhone}</span></div>
                    </div>
                  </div>

                  <div className="bg-white p-5 rounded-2xl border border-pbl-sand-border shadow-sm">
                    <h4 className="text-xs font-black text-pbl-sand-dark uppercase tracking-widest mb-4 flex items-center gap-2">
                      <FileText className="w-4 h-4" /> Commercial Plan
                    </h4>
                    <div className="space-y-3 text-sm">
                      <div><span className="font-semibold text-pbl-sand-dark">Billing Cycle:</span> <span className="font-bold text-pbl-green-dark">{selectedRequest.billingCycle}</span></div>
                      <div><span className="font-semibold text-pbl-sand-dark">Duration:</span> <span className="font-bold text-pbl-green-dark">{selectedRequest.contractDuration} Months</span></div>
                      <div><span className="font-semibold text-pbl-sand-dark">Seats:</span> <span className="font-bold text-pbl-green-dark">{selectedRequest.estimatedSeatCount} Estimated</span></div>
                    </div>
                  </div>
                </div>

                {/* Invoicing and Payment Status */}
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Invoice Box */}
                  <div className="bg-pbl-green-dark p-6 rounded-2xl border border-pbl-green-main/30 text-white shadow-xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-10">
                      <FileText className="w-24 h-24" />
                    </div>
                    <h4 className="text-xs font-black text-pbl-green-main uppercase tracking-widest mb-4 relative z-10">
                      Invoice Details
                    </h4>
                    {selectedRequest.invoice ? (
                      <div className="space-y-3 text-sm relative z-10">
                        <div><span className="font-semibold text-pbl-sand-dark/80">Invoice #:</span> <span className="font-bold text-white">{selectedRequest.invoice.invoiceNumber}</span></div>
                        <div><span className="font-semibold text-pbl-sand-dark/80">Date:</span> <span className="font-bold text-white">{new Date(selectedRequest.invoice.invoiceDate).toLocaleDateString()}</span></div>
                        <div><span className="font-semibold text-pbl-sand-dark/80">Due:</span> <span className="font-bold text-pbl-danger-main">{new Date(selectedRequest.invoice.dueDate).toLocaleDateString()}</span></div>
                        <div><span className="font-semibold text-pbl-sand-dark/80">Status:</span> <span className="font-bold text-white">{selectedRequest.invoice.status}</span></div>
                        <div className="pt-4 mt-2 border-t border-white/10 flex justify-between items-end">
                          <span className="font-bold text-pbl-sand-dark">Grand Total:</span>
                          <span className="text-3xl font-black text-pbl-green-main leading-none">
                            ₹{selectedRequest.invoice.grandTotal}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="py-8 text-center text-sm font-bold text-white/50 relative z-10">
                        No invoice has been generated for this request yet.
                      </div>
                    )}
                  </div>

                  {/* Payment Box */}
                  <div className="bg-white p-6 rounded-2xl border border-pbl-sand-border shadow-sm">
                    <h4 className="text-xs font-black text-pbl-sand-dark uppercase tracking-widest mb-4">
                      Payment Verification
                    </h4>
                    {selectedRequest.payment ? (
                      <div className="space-y-3 text-sm">
                        <div><span className="font-semibold text-pbl-sand-dark">Payment ID:</span> <span className="font-bold text-pbl-green-dark">{selectedRequest.payment.id}</span></div>
                        <div><span className="font-semibold text-pbl-sand-dark">Method:</span> <span className="font-bold text-pbl-green-dark">{selectedRequest.payment.paymentMethod}</span></div>
                        <div><span className="font-semibold text-pbl-sand-dark">Reference:</span> <span className="font-bold text-pbl-green-dark">{selectedRequest.payment.transactionReference}</span></div>
                        <div><span className="font-semibold text-pbl-sand-dark">Status:</span>
                          <span className={`ml-2 inline-block px-2.5 py-1 rounded-lg text-[9px] font-black border uppercase tracking-wider ${selectedRequest.payment.status === "VERIFIED"
                              ? "bg-pbl-green-light border-pbl-green-main/30 text-pbl-green-dark"
                              : "bg-[#FDF6E9] border-pbl-green-medium/25 text-pbl-green-medium"
                            }`}>
                            {selectedRequest.payment.status}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="py-8 text-center text-sm font-bold text-pbl-sand-dark bg-[#FAF7F5] rounded-xl border border-dashed border-pbl-sand-border h-full flex items-center justify-center">
                        <div>
                          <Clock className="w-8 h-8 text-pbl-sand-dark/50 mx-auto mb-2" />
                          <p>No payment submitted yet.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ACTION MODAL OVERLAY */}
      <AnimatePresence>
        {actionModal !== 'none' && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => actionModal !== 'success' && setActionModal('none')}
              className="absolute inset-0 bg-pbl-green-dark/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 10 }}
              className="relative bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-pbl-sand-border"
            >
              {actionModal === 'approve' && (
                <div className="text-center">
                  <CheckCircle className="w-16 h-16 text-pbl-green-main mx-auto mb-4" />
                  <h3 className="text-xl font-black text-pbl-green-dark mb-2">Confirm Approval</h3>
                  <p className="text-sm text-pbl-sand-dark mb-6">
                    Are you sure you want to approve this request? This will automatically provision the organization and coordinator accounts.
                  </p>
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={() => setActionModal('none')}
                      disabled={actionLoading}
                      className="px-5 py-2.5 rounded-xl font-bold text-sm text-pbl-sand-dark bg-[#FAF7F5] border border-pbl-sand-border hover:bg-pbl-sand-border disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmApprove}
                      disabled={actionLoading}
                      className="px-5 py-2.5 rounded-xl font-bold text-sm text-white bg-pbl-green-main hover:bg-pbl-green-dark disabled:opacity-50 flex items-center gap-2"
                    >
                      {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
                      Yes, Approve
                    </button>
                  </div>
                </div>
              )}

              {actionModal === 'verify' && (
                <div className="text-center">
                  <CheckCircle className="w-16 h-16 text-pbl-green-main mx-auto mb-4" />
                  <h3 className="text-xl font-black text-pbl-green-dark mb-2">Verify Payment</h3>
                  <p className="text-sm text-pbl-sand-dark mb-6">
                    Are you sure you want to verify this payment? Upon verification, you can provision slots.
                  </p>
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={() => setActionModal('none')}
                      disabled={actionLoading}
                      className="px-5 py-2.5 rounded-xl font-bold text-sm text-pbl-sand-dark bg-[#FAF7F5] border border-pbl-sand-border hover:bg-pbl-sand-border disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleVerifyPayment}
                      disabled={actionLoading}
                      className="px-5 py-2.5 rounded-xl font-bold text-sm text-white bg-pbl-green-main hover:bg-pbl-green-dark disabled:opacity-50 flex items-center gap-2"
                    >
                      {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
                      Yes, Verify
                    </button>
                  </div>
                </div>
              )}

              {actionModal === 'provision' && (
                <div className="text-center">
                  <CheckCircle className="w-16 h-16 text-pbl-green-main mx-auto mb-4" />
                  <h3 className="text-xl font-black text-pbl-green-dark mb-2">Provision Enterprise</h3>
                  <p className="text-sm text-pbl-sand-dark mb-6">
                    Are you sure you want to provision this enterprise? This will approve the request, allocate programs, and send invitation emails.
                  </p>
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={() => setActionModal('none')}
                      disabled={actionLoading}
                      className="px-5 py-2.5 rounded-xl font-bold text-sm text-pbl-sand-dark bg-[#FAF7F5] border border-pbl-sand-border hover:bg-pbl-sand-border disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleProvision}
                      disabled={actionLoading}
                      className="px-5 py-2.5 rounded-xl font-bold text-sm text-white bg-pbl-green-main hover:bg-pbl-green-dark disabled:opacity-50 flex items-center gap-2"
                    >
                      {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
                      Yes, Provision
                    </button>
                  </div>
                </div>
              )}

              {actionModal === 'reject' && (
                <div>
                  <h3 className="text-xl font-black text-pbl-green-dark mb-2">Reject Request</h3>
                  <p className="text-sm text-pbl-sand-dark mb-4">Please provide a reason for rejecting this request.</p>
                  <textarea
                    value={rejectRemarks}
                    onChange={(e) => setRejectRemarks(e.target.value)}
                    placeholder="Enter rejection remarks..."
                    className="w-full p-3 rounded-xl border border-pbl-sand-border/50 bg-[#FAF7F5] focus:outline-none focus:ring-2 focus:ring-pbl-green-main/30 mb-6 text-sm resize-none h-32"
                  />
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => setActionModal('none')}
                      disabled={actionLoading}
                      className="px-5 py-2.5 rounded-xl font-bold text-sm text-pbl-sand-dark bg-[#FAF7F5] border border-pbl-sand-border hover:bg-pbl-sand-border disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleReject}
                      disabled={actionLoading || !rejectRemarks.trim()}
                      className="px-5 py-2.5 rounded-xl font-bold text-sm text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
                      Submit Rejection
                    </button>
                  </div>
                </div>
              )}

              {actionModal === 'success' && (
                <div className="text-center">
                  <div className="w-16 h-16 bg-pbl-green-light rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-pbl-green-main" />
                  </div>
                  <h3 className="text-xl font-black text-pbl-green-dark mb-2">Success!</h3>
                  <p className="text-sm text-pbl-sand-dark mb-6">The enterprise request has been processed successfully.</p>
                  <button
                    onClick={() => {
                      setActionModal('none');
                      setSelectedRequest(null);
                    }}
                    className="w-full px-5 py-3 rounded-xl font-bold text-sm text-white bg-pbl-green-main hover:bg-pbl-green-dark"
                  >
                    Done
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

const UsersListView: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<any>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await getAllUsers();
        // Filter out super admin
        const filtered = data.filter((u: any) => {
          const email = (u.username || u.email || "").toLowerCase();
          return email !== "admin@sthitih.edu" && email !== "admin@sthiti.edu";
        });
        setUsers(filtered);
      } catch (err) {
        console.error("Failed to load users", err);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const filteredUsers = users.filter((u) => {
    const searchLower = searchQuery.toLowerCase();
    const username = (u.username || "").toLowerCase();
    const fullName = `${u.Firstname || ""} ${u.lastname || ""}`.toLowerCase();
    const email = (u.email || u.username || "").toLowerCase();
    const role = (u.role || "User").toLowerCase();
    const institute = (u.institute || "").toLowerCase();
    return username.includes(searchLower) || fullName.includes(searchLower) || email.includes(searchLower) || role.includes(searchLower) || institute.includes(searchLower);
  });

  return (
    <>
      <div className="bg-white border border-pbl-sand-border/40 rounded-2xl p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h3 className="text-lg font-bold text-pbl-green-dark uppercase tracking-wider">Registered Users</h3>
            <p className="text-xs font-bold text-pbl-sand-dark mt-0.5">Total: {filteredUsers.length} active platform accounts</p>
          </div>
          <div className="relative w-full sm:w-72">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-pbl-sand-dark">
              <Search className="w-4 h-4" />
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, role..."
              className="w-full pl-9 pr-4 py-2 bg-pbl-sand-bg/40 border border-pbl-sand-border rounded-xl text-xs font-semibold text-pbl-green-dark placeholder-pbl-sand-dark/50 focus:outline-none focus:ring-1 focus:ring-pbl-green-main focus:bg-white transition-all"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center p-8"><RefreshCw className="w-8 h-8 animate-spin text-pbl-green-medium" /></div>
        ) : (
          <div className="overflow-x-auto border border-pbl-sand-border/40 rounded-xl bg-white shadow-sm">
            <table className="min-w-full divide-y divide-pbl-sand-border/30">
              <thead className="bg-[#FAF7F5]">
                <tr>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-pbl-green-dark uppercase tracking-wider">Name / Username</th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-pbl-green-dark uppercase tracking-wider">Email</th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-pbl-green-dark uppercase tracking-wider">Role</th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-pbl-green-dark uppercase tracking-wider">Institution</th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-pbl-green-dark uppercase tracking-wider">Joined Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-pbl-sand-border/20 bg-white">
                {filteredUsers.map((u) => {
                  const fullName = `${u.Firstname || ""} ${u.lastname || ""}`.trim() || u.username || "Unknown User";
                  const dateStr = u.createdAt ? new Date(u.createdAt).toLocaleDateString("en-IN", {
                    year: 'numeric', month: 'short', day: 'numeric'
                  }) : "N/A";
                  return (
                    <tr
                      key={u.id || u.userId}
                      onClick={() => setSelectedUser(u)}
                      className="hover:bg-pbl-green-light/20 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-pbl-green-light border border-pbl-green-main/20 flex items-center justify-center font-bold text-xs text-pbl-green-dark">
                            {fullName.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="text-xs font-black text-pbl-green-dark">{fullName}</div>
                            {u.username && <div className="text-[10px] text-pbl-sand-dark font-semibold">@{u.username}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-pbl-sand-dark">
                        {u.email || u.username || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-black tracking-wider uppercase bg-pbl-green-light border border-pbl-green-main/10 text-pbl-green-main">
                          {u.role || "User"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-pbl-green-dark/80">
                        {u.institute || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-pbl-sand-dark">
                        {dateStr}
                      </td>
                    </tr>
                  );
                })}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-xs font-bold text-pbl-sand-dark bg-[#FAF7F5]">
                      No users found matching your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AnimatePresence>
        {selectedUser && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedUser(null)}
              className="fixed inset-0 bg-pbl-green-dark/60 backdrop-blur-md"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FAF7F5] rounded-3xl shadow-2xl p-6 sm:p-8 border border-white/20 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            >
              <div className="mb-6 pb-4 border-b border-pbl-sand-border/50 flex justify-between items-start">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-pbl-green-main text-white flex items-center justify-center font-black text-xl shadow-md border border-white/40">
                    {(`${selectedUser.Firstname || ""} ${selectedUser.lastname || ""}`).trim().charAt(0).toUpperCase() || "?"}
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-pbl-green-dark leading-tight">
                      {`${selectedUser.Firstname || ""} ${selectedUser.lastname || ""}`.trim() || selectedUser.username}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className="px-2 py-0.5 bg-pbl-green-light border border-pbl-green-main/10 text-[9px] font-black tracking-wider uppercase rounded text-pbl-green-main">
                        {selectedUser.role || "User"}
                      </span>
                      {selectedUser.username && (
                        <span className="text-xs text-pbl-sand-dark font-semibold">
                          @{selectedUser.username}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="p-2 hover:bg-pbl-sand-border/30 rounded-xl text-pbl-sand-dark transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Contact & Personal details */}
                <div className="bg-white border border-pbl-sand-border/40 rounded-2xl p-5 shadow-sm space-y-4">
                  <h4 className="text-[10px] font-black text-pbl-green-dark uppercase tracking-wider border-b border-pbl-sand-border/20 pb-2">Profile Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                    <div>
                      <p className="text-[10px] text-pbl-sand-dark font-extrabold uppercase tracking-wide">Email</p>
                      <p className="font-bold text-pbl-green-dark mt-0.5">{selectedUser.email || selectedUser.username || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-pbl-sand-dark font-extrabold uppercase tracking-wide">Gender</p>
                      <p className="font-bold text-pbl-green-dark mt-0.5">{selectedUser.gender || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-pbl-sand-dark font-extrabold uppercase tracking-wide">Country</p>
                      <p className="font-bold text-pbl-green-dark mt-0.5">{selectedUser.country || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-pbl-sand-dark font-extrabold uppercase tracking-wide">Pincode</p>
                      <p className="font-bold text-pbl-green-dark mt-0.5">{selectedUser.pincode || "N/A"}</p>
                    </div>
                  </div>
                </div>

                {/* Academic/Professional Info */}
                <div className="bg-white border border-pbl-sand-border/40 rounded-2xl p-5 shadow-sm space-y-4">
                  <h4 className="text-[10px] font-black text-pbl-green-dark uppercase tracking-wider border-b border-pbl-sand-border/20 pb-2">Academic Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                    <div>
                      <p className="text-[10px] text-pbl-sand-dark font-extrabold uppercase tracking-wide">Institute</p>
                      <p className="font-bold text-pbl-green-dark mt-0.5">{selectedUser.institute || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-pbl-sand-dark font-extrabold uppercase tracking-wide">Course / Branch</p>
                      <p className="font-bold text-pbl-green-dark mt-0.5">{selectedUser.course || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-pbl-sand-dark font-extrabold uppercase tracking-wide">Year of Passing</p>
                      <p className="font-bold text-pbl-green-dark mt-0.5">{selectedUser.yearofpassing || "N/A"}</p>
                    </div>
                  </div>
                </div>

                {/* Skills & About */}
                <div className="bg-white border border-pbl-sand-border/40 rounded-2xl p-5 shadow-sm space-y-4">
                  <h4 className="text-[10px] font-black text-pbl-green-dark uppercase tracking-wider border-b border-pbl-sand-border/20 pb-2">Skills & About</h4>
                  <div className="space-y-3 text-xs">
                    <div>
                      <p className="text-[10px] text-pbl-sand-dark font-extrabold uppercase tracking-wide mb-1">About</p>
                      <p className="font-bold text-pbl-green-dark/80 leading-relaxed bg-[#FAF7F5] p-3 rounded-xl border border-pbl-sand-border/30">
                        {selectedUser.about || "No profile bio available."}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-pbl-sand-dark font-extrabold uppercase tracking-wide mb-1">Skills</p>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedUser.skills && Array.isArray(selectedUser.skills) && selectedUser.skills.length > 0 ? (
                          selectedUser.skills.map((skill: string, index: number) => (
                            <span key={index} className="px-2.5 py-1 bg-pbl-green-light border border-pbl-green-main/10 rounded-lg font-bold text-pbl-green-dark">
                              {skill}
                            </span>
                          ))
                        ) : (
                          <span className="text-pbl-sand-dark italic font-semibold">No skills specified.</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Links */}
                <div className="bg-white border border-pbl-sand-border/40 rounded-2xl p-5 shadow-sm space-y-4">
                  <h4 className="text-[10px] font-black text-pbl-green-dark uppercase tracking-wider border-b border-pbl-sand-border/20 pb-2">Professional Links</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    {selectedUser.links && Object.entries(selectedUser.links).map(([key, val]: any) => {
                      if (!val) return null;
                      return (
                        <a
                          key={key}
                          href={val}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between p-3 border border-pbl-sand-border/50 rounded-xl hover:bg-pbl-green-light/20 text-pbl-green-dark font-bold transition-colors"
                        >
                          <span className="capitalize">{key}</span>
                          <ExternalLink className="w-3.5 h-3.5 text-pbl-sand-dark" />
                        </a>
                      );
                    })}
                    {(!selectedUser.links || Object.values(selectedUser.links).every(v => !v)) && (
                      <p className="text-pbl-sand-dark italic font-semibold col-span-full">No professional links added.</p>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export const SuperAdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"quotes" | "users">("quotes");

  return (
    <div className="min-h-screen bg-[#FAF7F5] font-sans antialiased text-pbl-green-dark">
      {/* DASHBOARD VIEW */}
      <div className="flex min-h-screen relative overflow-x-hidden">
        {/* Custom Super Admin Sidebar (Fixed left, width 80px / w-20) */}
        <aside className="fixed inset-y-0 left-0 z-30 w-20 flex flex-col h-screen bg-pbl-sand-bg items-center py-4 justify-between border-r border-pbl-sand-border">
          {/* Logo Spacer */}
          <div className="h-16 flex items-center justify-center">
            <div className="w-10 h-10 rounded-xl bg-pbl-green-main flex items-center justify-center shadow-md">
              <Shield className="w-5.5 h-5.5 text-white" />
            </div>
          </div>

          {/* Nav */}
          <nav className="flex-1 flex flex-col items-center pt-8 space-y-5 w-full">
            <button
              onClick={() => setActiveTab("quotes")}
              className="relative group flex items-center justify-center w-full focus:outline-none"
            >
              <div className={`flex items-center justify-center w-12 h-12 rounded-xl transition-all duration-200 cursor-pointer ${
                activeTab === "quotes"
                  ? "text-white bg-pbl-green-main shadow-md shadow-pbl-green-main/30 border-l-2 border-pbl-green-medium"
                  : "text-pbl-green-main/70 hover:text-pbl-green-dark hover:bg-pbl-green-light/40"
              }`}>
                <FileText className="w-5.5 h-5.5" />
              </div>
              <div className="absolute left-20 bg-pbl-green-dark border border-pbl-green-main/30 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-xl opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-250 pointer-events-none whitespace-nowrap z-50">
                Enterprise Quotes
              </div>
            </button>

            <button
              onClick={() => setActiveTab("users")}
              className="relative group flex items-center justify-center w-full focus:outline-none"
            >
              <div className={`flex items-center justify-center w-12 h-12 rounded-xl transition-all duration-200 cursor-pointer ${
                activeTab === "users"
                  ? "text-white bg-pbl-green-main shadow-md shadow-pbl-green-main/30 border-l-2 border-pbl-green-medium"
                  : "text-pbl-green-main/70 hover:text-pbl-green-dark hover:bg-pbl-green-light/40"
              }`}>
                <Users className="w-5.5 h-5.5" />
              </div>
              <div className="absolute left-20 bg-pbl-green-dark border border-pbl-green-main/30 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-xl opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-250 pointer-events-none whitespace-nowrap z-50">
                Users List
              </div>
            </button>
          </nav>

          {/* Back to Dashboard */}
          <div className="pb-4">
            <a
              href="/dashboard"
              className="w-11 h-11 rounded-full bg-pbl-sand-light hover:bg-pbl-sand-main text-pbl-green-dark flex items-center justify-center border border-pbl-sand-border shadow-sm transition-all duration-250 cursor-pointer active:scale-95"
              title="Back to Dashboard"
            >
              <ArrowRight className="w-5 h-5 rotate-180" />
            </a>
          </div>
        </aside>

        {/* Custom Super Admin Header (Fixed top, h-16, shifted left by ml-20) */}
        <header className="fixed top-0 left-0 right-0 z-40 h-16 ml-20 bg-pbl-sand-bg border-b border-pbl-sand-border/70 px-6 flex items-center justify-between">
          <div className="flex flex-col justify-center">
            <h1 className="text-lg font-black tracking-tight leading-tight text-pbl-green-dark">
              Task Management — Super Admin
            </h1>
            <span className="text-xs font-semibold uppercase tracking-wider text-pbl-sand-dark">
              Enterprise Quotes & User Management
            </span>
          </div>

          <div className="flex items-center space-x-4">
            <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-pbl-sand-border text-[11px] font-extrabold uppercase tracking-wider rounded-xl text-pbl-green-main">
              <span className="w-1.5 h-1.5 bg-pbl-green-medium rounded-full animate-ping" /> System Live
            </span>
            <div className="h-6 w-px bg-pbl-sand-border/40" />
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-pbl-green-dark border border-pbl-green-main/30 flex items-center justify-center font-bold text-xs text-white">
                SA
              </div>
              <div className="hidden md:block text-left">
                <p className="text-xs font-black text-pbl-green-dark leading-tight">Super Administrator</p>
                <p className="text-[9px] font-bold text-pbl-sand-dark">Task Management</p>
              </div>
            </div>
          </div>
        </header>

        {/* Main Dashboard Area */}
        <div className="flex-grow min-w-0 min-h-screen w-full pt-16 ml-20 bg-[#FAF7F5]">
          <main className="p-6 max-w-[1600px] mx-auto space-y-6">
            {activeTab === "quotes" ? <EnterpriseQuotesView /> : <UsersListView />}
          </main>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
